import { useState } from "react"
import { COUNTRIES, EXAM_BODIES, CLASS_LEVELS, getEligibleClasses } from "@/data/educationSystems"

export function ProfileStep({ defaultData = {}, onNext }) {
  const [firstName, setFirstName] = useState(defaultData.firstName ?? "")
  const [lastName, setLastName] = useState(defaultData.lastName ?? "")
  const [country, setCountry] = useState(defaultData.country ?? "")
  const [examBody, setExamBody] = useState(defaultData.examBody ?? "")
  const [classLevel, setClassLevel] = useState(defaultData.classLevel ?? "")
  const [extraClasses, setExtraClasses] = useState([])   // additional classes below current

  const examBodies = country ? EXAM_BODIES[country] ?? [] : []
  const allClassLevels = examBody ? CLASS_LEVELS[examBody] ?? [] : []
  const eligibleClasses = classLevel ? getEligibleClasses(examBody, classLevel) : []
  // eligibleClasses[0] is the current class (always selected, not toggleable)
  const optionalClasses = eligibleClasses.slice(1)

  const toggleExtra = (cls) => {
    setExtraClasses((prev) =>
      prev.includes(cls) ? prev.filter((c) => c !== cls) : [...prev, cls]
    )
  }

  const handleCountryChange = (c) => {
    setCountry(c)
    setExamBody("")
    setClassLevel("")
    setExtraClasses([])
  }

  const handleExamBodyChange = (e) => {
    setExamBody(e)
    setClassLevel("")
    setExtraClasses([])
  }

  const handleClassLevelChange = (cl) => {
    setClassLevel(cl)
    setExtraClasses([])
  }

  const canContinue = firstName.trim() && lastName.trim() && country && examBody && classLevel

  const handleNext = () => {
    const classes = [classLevel, ...extraClasses.filter((c) => c !== classLevel)]
    onNext({ firstName: firstName.trim(), lastName: lastName.trim(), country, examBody, classLevel, classes })
  }

  return (
    <div className="space-y-6">
      <div className="text-center space-y-1">
        <h2 className="text-2xl font-black text-slate-900">Tell us about you</h2>
        <p className="text-sm text-slate-500">We'll personalise everything to fit your curriculum.</p>
      </div>

      {/* Name */}
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1">
          <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">First Name</label>
          <input
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            placeholder="e.g. Adaeze"
            className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
          />
        </div>
        <div className="space-y-1">
          <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Last Name</label>
          <input
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            placeholder="e.g. Okonkwo"
            className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
          />
        </div>
      </div>

      {/* Education System */}
      <div className="space-y-3">
        <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wide">Education System</h3>

        <div className="space-y-1">
          <label className="text-xs font-semibold text-slate-500">Country</label>
          <select
            value={country}
            onChange={(e) => handleCountryChange(e.target.value)}
            className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400 bg-white"
          >
            <option value="">Select country</option>
            {COUNTRIES.map((c) => (
              <option key={c.code} value={c.code}>{c.name}</option>
            ))}
          </select>
        </div>

        {country && (
          <div className="space-y-1">
            <label className="text-xs font-semibold text-slate-500">Exam System</label>
            <select
              value={examBody}
              onChange={(e) => handleExamBodyChange(e.target.value)}
              className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400 bg-white"
            >
              <option value="">Select system</option>
              {examBodies.map((e) => (
                <option key={e.code} value={e.code}>{e.name}</option>
              ))}
            </select>
          </div>
        )}

        {examBody && (
          <div className="space-y-1">
            <label className="text-xs font-semibold text-slate-500">Current Class</label>
            <select
              value={classLevel}
              onChange={(e) => handleClassLevelChange(e.target.value)}
              className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400 bg-white"
            >
              <option value="">Select class</option>
              {allClassLevels.map((cl) => (
                <option key={cl} value={cl}>{cl}</option>
              ))}
            </select>
          </div>
        )}

        {/* Additional classes below current (optional, max 2) */}
        {optionalClasses.length > 0 && (
          <div className="space-y-2">
            <label className="text-xs font-semibold text-slate-500">
              Also studying below? <span className="text-slate-400 font-normal">(optional, max 2)</span>
            </label>
            <div className="flex flex-wrap gap-2">
              {optionalClasses.map((cls) => {
                const selected = extraClasses.includes(cls)
                const disabled = !selected && extraClasses.length >= 2
                return (
                  <button
                    key={cls}
                    type="button"
                    disabled={disabled}
                    onClick={() => toggleExtra(cls)}
                    className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-all ${
                      selected
                        ? "bg-indigo-600 border-indigo-600 text-white"
                        : disabled
                        ? "bg-slate-50 border-slate-200 text-slate-300 cursor-not-allowed"
                        : "bg-white border-slate-200 text-slate-600 hover:border-indigo-300"
                    }`}
                  >
                    {cls}
                  </button>
                )
              })}
            </div>
          </div>
        )}
      </div>

      <button
        disabled={!canContinue}
        onClick={handleNext}
        className="w-full bg-indigo-600 hover:bg-indigo-700 disabled:opacity-40 text-white font-bold py-3 rounded-xl text-sm transition-all active:scale-95"
      >
        Continue
      </button>
    </div>
  )
}
